import { useState, useEffect } from 'react';
import { observer } from 'mobx-react';

import useStores from '$src/hooks/useStores';
import useFeature from '$src/hooks/useFeature';
import useLocationContext from '$src/hooks/useLocationContext';
import { handleBackToRingDashboard } from '$src/utils/handleBack';
import { redirectToFreeTrial, redirectToOnboardingIntroduction } from '$src/utils/redirect';
import { vsgModeStatus } from '$src/api/constants';
import { TAPPING_RESUME_BUTTON } from '$src/constants/actions';

import Layout from '$src/components/Layout';
import Banner from '$src/components/Banner';
import SettingsSiderItems from './SettingsSiderItems';
import SettingsItems from './SettingsItems';

function StarkSettings() {
  const { sessionStore, modesStore, locationContextStore, statsStore } = useStores();
  const locationId = useLocationContext();
  const { isMobile, isLoading } = sessionStore;
  const { isPaused, changeVSGState, vsgErrorState } = modesStore;
  const { isFreeTrialAvailable, location, locationOwnerId } = locationContextStore;
  const { sendStats } = statsStore;
  const [hideBanner, setHideBanner] = useState(false);
  const [isLocationPausedFeatureEnabled] = useFeature('stark-feature-next-location-paused');
  const isBannerVisible = isPaused && !hideBanner && isLocationPausedFeatureEnabled;
  const freeTrialRedirect = isFreeTrialAvailable && locationId;
  const privateRedirect = locationId && location?.hasEntitlements === false;
  const isSetupIncomplete = vsgErrorState === vsgModeStatus.INCOMPLETE;

  useEffect(() => {
    if (locationOwnerId && locationId) {
      modesStore.fetchVSGModeStatus(locationOwnerId, locationId);
      modesStore.fetchVSGState(locationOwnerId, locationId);
      modesStore.fetchMonitoringStatus(locationOwnerId, locationId);
    }
  }, [locationId, modesStore, locationOwnerId, isLoading]);

  if (freeTrialRedirect) {
    redirectToFreeTrial(locationId);
    return null;
  }
  if (privateRedirect) {
    handleBackToRingDashboard(isMobile);
    return null;
  }

  if (isSetupIncomplete) {
    redirectToOnboardingIntroduction(locationId);
  }

  const onUnpauseButton = () => {
    changeVSGState(locationOwnerId, locationId, false);
    sendStats(TAPPING_RESUME_BUTTON);
  };

  return (
    <Layout
      sider={<SettingsSiderItems />}
      banner={
        <Banner
          buttonText="Resume"
          handleButton={onUnpauseButton}
          handleClose={() => setHideBanner(true)}
          visible={isBannerVisible}
          sessionKey="pause"
          clearKey={!isPaused}
          handleClearKey={() => setHideBanner(false)}
        >
          Guard monitoring is currently paused at your location.
        </Banner>
      }
    >
      <SettingsItems />
    </Layout>
  );
}

export default observer(StarkSettings);
